import React, { useEffect, useId, useRef } from "react";
import EditorJS, {
  type OutputData,
  type ToolConstructable,
} from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import Delimiter from "@editorjs/delimiter";
import Embed from "@editorjs/embed";
import ImageTool from "@editorjs/image";
import Table from "@editorjs/table";
import Checklist from "@editorjs/checklist";
import { fileService } from "@/services/file-service";
import {
  getEmptyRichEditorData,
  renderRichEditorContent,
} from "./rich-editor.utils";

const headerTool = Header as unknown as ToolConstructable;
const embedTool = Embed as unknown as ToolConstructable;
const imageTool = ImageTool as unknown as ToolConstructable;
const tableTool = Table as unknown as ToolConstructable;
const checklistTool = Checklist as unknown as ToolConstructable;

interface RichEditorProps {
  value: OutputData;
  onChange: (data: OutputData, html: string) => void;
  placeholder?: string;
}

export const RichEditor: React.FC<RichEditorProps> = ({
  value,
  onChange,
  placeholder = "Start typing...",
}) => {
  const holderId = useId().replace(/:/g, "-");
  const editorRef = useRef<EditorJS | null>(null);
  const onChangeRef = useRef(onChange);
  const isSettingDataRef = useRef(false);
  const latestValueRef = useRef(value);
  const serializedValueRef = useRef(JSON.stringify(value));

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  useEffect(() => {
    let isMounted = true;

    const editor = new EditorJS({
      holder: holderId,
      autofocus: true,
      placeholder,
      inlineToolbar: ["bold", "italic", "link"],
      data: latestValueRef.current,
      tools: {
        header: {
          class: headerTool,
          inlineToolbar: true,
          config: {
            levels: [1, 2, 3, 4],
            defaultLevel: 2,
          },
        },
        list: {
          class: List,
          inlineToolbar: true,
        },
        quote: {
          class: Quote,
          inlineToolbar: true,
        },
        delimiter: Delimiter,
        embed: {
          class: embedTool,
          inlineToolbar: false,
          config: {
            services: {
              youtube: true,
              vimeo: true,
            },
          },
        },
        image: {
          class: imageTool,
          config: {
            captionPlaceholder: "Add an optional caption",
            features: {
              border: true,
              background: true,
              stretch: true,
              caption: "optional",
            },
            uploader: {
              async uploadByFile(file: File) {
                const fileId =
                  await fileService.uploadFileWithPresignedUrl(file);
                const url = await fileService.getSignedUrl(fileId);

                return {
                  success: 1,
                  file: {
                    url,
                    fileId,
                  },
                };
              },
              async uploadByUrl(url: string) {
                return {
                  success: 1,
                  file: {
                    url,
                  },
                };
              },
            },
          },
        },
        table: {
          class: tableTool,
          inlineToolbar: true,
          config: {
            rows: 2,
            cols: 2,
            maxRows: 10,
            maxCols: 6,
            withHeadings: false,
          },
        },
        checklist: {
          class: checklistTool,
          inlineToolbar: true,
        },
      },
      async onChange() {
        if (!editorRef.current || isSettingDataRef.current) {
          return;
        }

        const nextData = await editorRef.current.save();
        serializedValueRef.current = JSON.stringify(nextData);
        onChangeRef.current(nextData, renderRichEditorContent(nextData));
      },
    });

    editor.isReady
      .then(() => {
        if (!isMounted) {
          editor.destroy();
          return;
        }

        editorRef.current = editor;
        const serializedValue = JSON.stringify(latestValueRef.current);

        if (serializedValue === serializedValueRef.current) {
          return;
        }

        isSettingDataRef.current = true;

        editor
          .render(latestValueRef.current ?? getEmptyRichEditorData())
          .finally(() => {
            serializedValueRef.current = serializedValue;
            isSettingDataRef.current = false;
          });
      })
      .catch((error) => {
        console.error("Failed to initialize RichEditor", error);
      });

    return () => {
      isMounted = false;
      editorRef.current?.destroy();
      editorRef.current = null;
    };
  }, [holderId, placeholder]);

  useEffect(() => {
    const editor = editorRef.current;

    if (!editor) {
      return;
    }

    const serializedValue = JSON.stringify(value);

    if (serializedValue === serializedValueRef.current) {
      return;
    }

    isSettingDataRef.current = true;

    editor.render(value ?? getEmptyRichEditorData()).finally(() => {
      serializedValueRef.current = serializedValue;
      isSettingDataRef.current = false;
    });
  }, [value]);

  return <div id={holderId} />;
};
